import { css } from '@emotion/react'
import { format } from 'date-fns'
import { graphql } from 'gatsby'
import { ComponentPropsWithoutRef, ElementType } from 'react'

import { GatsbyImageFocused } from '@/features/common/'
import { LightboxLink } from '@/features/lightbox'
import { bezier, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

interface Props extends ComponentPropsWithoutRef<'a'> {
  data?: Queries.NewsArticleThumbnailFragment | null
  layout?: 'HOME_PAGE' | 'PAGE'
  headingLevel?: 2 | 3
}

export const NewsArticleThumbnail = ({
  data,
  layout = 'PAGE',
  headingLevel = 2,
  ...props
}: Props): JSX.Element => {
  const Heading = `h${headingLevel}` as ElementType
  const getPublicationDate = () => {
    const date = data?.meta.createdAt && new Date(data.meta.createdAt)
    return date && format(date, 'MMMM d, y')
  }
  const styles = {
    link: css`
      text-decoration: none;
    `,
    container: css`
      position: relative;
      min-height: 100%;
      &:before {
        content: '';
        position: absolute;
        left: -1rem;
        top: -1rem;
        width: calc(100% + 2rem);
        height: calc(100% + 2rem);
        transition: background-color 500ms ${bezier.easeOut};
      }
      @media (hover: hover) {
        a:hover &:before {
          background: ${colors.gray95};
        }
      }
      ${layout === 'HOME_PAGE' &&
      css`
        display: grid;
        grid-template-columns: 2fr 3fr;
        grid-gap: 1.25em;
        ${mq().ms} {
          grid-gap: 0.75em;
        }
      `}
    `,
    image: css`
      overflow: hidden;
      [data-gatsby-image-wrapper] {
        transition: transform 500ms ${bezier.easeOut};
      }
      @media (hover: hover) {
        a:hover & {
          [data-gatsby-image-wrapper] {
            transform: scale3d(1.05, 1.05, 1);
          }
        }
      }
      ${layout === 'HOME_PAGE' &&
      css`
        min-height: 100%;
      `}
    `,
    text: css`
      position: relative;
      color: ${colors.gray20};
      transition: color 300ms ease;
      ${layout === 'PAGE' &&
      css`
        position: relative;
        color: ${colors.gray20};
        position: relative;
        padding: 0em 0.5em 0.5em 1em;
        margin-top: 0.75rem;
        border-left: 2px solid ${colors.gray90};
        transition: border-color 300ms ease;
        height: 100%;
        @media (hover: hover) {
          a:hover & {
            border-color: ${colors.red};
          }
        }
      `}
    `,
    category: css`
      display: inline-block;
      text-transform: uppercase;
      font-size: var(--fs-14);
      font-weight: 450;
      color: ${colors.red};
      margin: 0.25em 0 0;
    `,
    divider: css`
      color: rgba(0, 0, 0, 0.25);
      font-size: var(--fs-14);
    `,
    date: css`
      display: inline-block;
      text-transform: uppercase;
      font-size: var(--fs-14);
      font-weight: 500;
      color: rgba(0, 0, 0, 0.5);
      ${layout === 'HOME_PAGE' &&
      css`
        color: ${colors.gray65};
        margin: 0.25em 0 0;
        transition: color 300ms ease;
        ${mq().ms} {
          font-size: 1rem;
        }
        @media (hover: hover) {
          a:hover & {
            color: ${colors.gray45};
          }
        }
      `}
    `,
    heading: css`
      font-family: var(--ff-body);
      margin: 0.25em 0;
      line-height: 1.25;
      ${layout === 'PAGE' &&
      css`
        font-family: var(--ff-display);
        font-size: var(--fs-30);
        margin: 0.25em 0 0.5em;
        line-height: 1.125;
      `}
      ${layout === 'HOME_PAGE' &&
      css`
        font-size: var(--fs-21);
        border-top: 2px solid ${colors.gray85};
        padding-top: 0.666em;
        margin-bottom: 0.25em;
        transition: all 300ms ease;
        @media (hover: hover) {
          a:hover & {
            border-color: ${colors.red};
          }
        }
        ${mq().ms} {
          font-size: var(--fs-18);
          font-weight: 450;
        }
      `}
    `,
  }

  return (
    <LightboxLink
      data={data}
      css={styles.link}
      {...props}
    >
      <div css={styles.container}>
        <GatsbyImageFocused
          css={styles.image}
          image={data?.thumbnailImage?.gatsbyImageData}
          aspectRatio={3 / 2}
          data={data?.thumbnailImage}
        />
        <div css={styles.text}>
          <div css={styles.category}>{data?.category?.name}</div>
          <span css={styles.divider}>&emsp;|&emsp;</span>
          <div css={styles.date}>{getPublicationDate()}</div>
          <Heading css={styles.heading}>{data?.heading}</Heading>
        </div>
      </div>
    </LightboxLink>
  )
}

export const NewsArticleThumbnailFragment = graphql`
  fragment NewsArticleThumbnail on DatoCmsNewsArticle {
    ...NewsArticle
    thumbnailImage {
      gatsbyImageData(
        width: 480
        sizes: "(max-width: 540px) 100vw, (max-width: 720px) 50vw, (max-width: 1080px) 33vw, 25vw"
        imgixParams: { ar: "3:2", fit: "crop", crop: "focalpoint" }
      )
      ...ImageFocalData
    }
  }
`
